import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { useFieldArray, useForm } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import { GetCities } from "../../../../api/masters/cityAPI";
import { GetAreaById, UpdateArea } from "../../../../api/masters/areaAPI";
import { GetOpdPrescriptionEntryById, UpdateOpdPrescriptionEntry } from "../../../../api/opd/opdPrescriptionEntryAPI";
import { GetCases } from "../../../../api/masters/caseAPI";
import { GetOpdEntries } from "../../../../api/opd/opdEntryAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  consultantDoctorId: yup.string().required("Consultant Doctor is required"),
  date: yup.date().required("Date is required"),
  diagnosis: yup.string().required("Diagnosis is required")
});

const DynamicFieldArray = ({ label, name, control, register }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name
  });

  return (
    <div className="col-12 mt-3">
      <label>{label}</label>

      {fields.map((item, index) => (
        <div className="d-flex mb-2" key={item.id}>
          <input
            className="form-control me-2"
            {...register(`${name}.${index}.value`)}
            placeholder={label}
          />

          <button
            type="button"
            className="btn btn-danger me-1"
            disabled={fields.length === 1}
            onClick={() => remove(index)}
          >
            <i className="fa fa-minus" />
          </button>

          <button
            type="button"
            className="btn btn-success"
            onClick={() => append({ value: "" })}
          >
            <i className="fa fa-plus" />
          </button>
        </div>
      ))}
    </div>
  );
};

const EditPrescriptionEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [doctors, setDoctors] = useState([]);
  const [opdEntries, setOpdEntries] = useState([]);
  const [cases, setCases] = useState([]);
  const [patientDetails, setPatientDetails] = useState({});

  const { id } = useParams();

  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      complaints: [{ value: "" }],
      pastHistory: [{ value: "" }],
      familyHistory: [{ value: "" }],
      examinations: [{ value: "" }]
    }
  });

  const fetchData = async () => {
    try {
      const response = await GetOpdPrescriptionEntryById(id); // Make sure login function returns token
      
      if (response.success) {
        Object.entries(response.data).forEach(([key, value]) => {
          if (["date",'nextAppointmentDate'].includes(key) && value) {
            setValue(key, value.split("T")[0]); // keeps YYYY-MM-DD
          } else {
            setValue(key, value);
          }
          if (key === "complaints") {
            setValue(key, value.map((item) => ({ value: item })));
          }
          if (key === "pastHistory") {
            setValue(key, value.map((item) => ({ value: item })));
          }
          if (key === "familyHistory") {
            setValue(key, value.map((item) => ({ value: item })));
          }
          if (key === "examinations") {
            setValue(key, value.map((item) => ({ value: item })));
          }
        });
        setValue('caseId', response.data?.caseId?._id || '');
        
        setPatientDetails(response.data?.opdEntryId || {});

        const doctorId = response.data?.opdEntryId?.consultantDoctorId?._id;

        const entries = await fetchOpdEntries(doctorId);
        setTimeout(() => {
          setValue("opdEntryId", response.data?.opdEntryId?._id || "");
        },100)
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  const fetchOpdEntries = async (consultantDoctorId) => {
      try {
        const response = await GetOpdEntries({ consultantDoctorId });
        if (response.success) {
          setOpdEntries(response.data);
        } else {
          showToast("error", response.message);
        }
      } catch (error) {
        showToast("error", error.message);
      }
    };

  useEffect(
    () => {
      if (!id) return;
      // Fetch existing data
      const loadAllData = async () => {
        try {
          const [consultantRes, caseRes] = await Promise.all([
            GetConsultants(),
            GetCases()
          ]);

          if (consultantRes.success) {
            setDoctors(consultantRes.data);
          } else {
            showToast("error", consultantRes.message);
          }

          if (caseRes.success) {
            setCases(caseRes.data);
          } else {
            showToast("error", caseRes.message);
          }

          // ✅ CALL AFTER ALL ABOVE ARE DONE
          fetchData();
        } catch (error) {
          console.error("Error loading master data:", error);
        }
      };
      loadAllData();
    },
    [id]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });
      const payload = {
        ...data,
        complaints: data.complaints.map(i => i.value).filter(Boolean),
        pastHistory: data.pastHistory.map(i => i.value).filter(Boolean),
        familyHistory: data.familyHistory.map(i => i.value).filter(Boolean),
        examinations: data.examinations.map(i => i.value).filter(Boolean)
      };
      const response = await UpdateOpdPrescriptionEntry(id, payload);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
        useKeyboardSubmit(handleSubmit, onSubmit);
        useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="OPD" title="Edit Prescription Entry" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Edit OPD Prescription</h5>
            <Link to="/opd/transaction/opd-prescription-entry/list">
              <button className="btn btn-primary" id="list">
                List Prescriptions
              </button>
              <UncontrolledTooltip target="list">
                List Prescriptions
              </UncontrolledTooltip>
            </Link>
          </div>

          <form
						ref={formRef}
						className="form theme-form"
						onSubmit={handleSubmit(onSubmit)}
					>
						<div className="card-body">
							<div className="row">

								{/* Doctor */}
								<div className="col-md-4">
									<div className="form-group">

										<label>Consultant Doctor *</label>
										<select
											className="form-control"
											{...register("consultantDoctorId", {
												onChange: e => {
													fetchOpdEntries(e.target.value);
												}
											})}
										>
											<option value="">Select Doctor</option>
											{doctors.map(d => (
												<option key={d._id} value={d._id}>
													{d.name}
												</option>
											))}
										</select>
										<p className="text-danger">
											{errors.consultantDoctorId?.message}
										</p>
									</div>
								</div>

								{/* Date */}
								<div className="col-md-4">
									<div className="form-group">

										<label>Date *</label>
										<input
											type="date"
											className="form-control"
											defaultValue={new Date().toISOString().split("T")[0]}
											min={new Date().toISOString().split("T")[0]}
											{...register("date")}
										/>
										<p className="text-danger">{errors.date?.message}</p>
									</div>
								</div>

								{/* Next Appointment */}
								<div className="col-md-4">
									<div className="form-group">

										<label>Next Appointment Date</label>
										<input
											type="date"
											className="form-control"
											min={new Date(Date.now() + 864e5).toISOString().split('T')[0]}
											{...register("nextAppointmentDate")}
										/>
									</div>
								</div>

								{/* OPD Entry */}
								<div className="col-md-4">
									<div className="form-group">

										<label>OPD Entry</label>
										<select className="form-control" {...register("opdEntryId", {
											onChange: (e) => {
												setPatientDetails(opdEntries.find(opd => opd._id === e.target.value));
											}
										})}>
											<option value="">Select OPD</option>
											{opdEntries.map(o => (
												<option key={o._id} value={o._id}>
													{o.opdNo}
												</option>
											))}
										</select>
									</div>
								</div>
							</div>
							<div className="col-12">
								<h5 className="mt-3 mb-3 text-primary">
									Patient Details
								</h5>
							</div>
							<div className="row">
								<div className="col-6">
									<div className="form-group">
										<label>UHID No</label>
										<input
											className="form-control"
											placeholder="UHID No"
											value={patientDetails?.patientId?.uhidNo}
											readOnly
										/>
									</div>
								</div>
								<div className="col-6">
									<div className="form-group">
										<label>Opd No</label>
										<input
											className="form-control"
											placeholder="Opd No"
											value={patientDetails?.opdNo}
											readOnly
										/>
									</div>
								</div>
								<div className="col-6">
									<div className="form-group">
										<label>Patient</label>
										<input
											className="form-control"
											placeholder="Patient Name"
											value={patientDetails?.patientId?.patientName}
											readOnly
										/>
									</div>
								</div>
								<div className="col-6">
									<div className="form-group">
										<label>guardian Name</label>
										<input
											className="form-control"
											placeholder="guardian Name"
											value={patientDetails?.patientId?.guardianName}
											readOnly
										/>
									</div>
								</div>
								<div className="col-6">
									<div className="form-group">
										<label>Age</label>
										<input
											className="form-control"
											placeholder="Patient Name"
											value={patientDetails?.patientId?.age}
											readOnly
										/>
									</div>
								</div>
								<div className="col-6">
									<div className="form-group">
										<label>Case</label>
										<input
											className="form-control"
											placeholder="Case"
											value={patientDetails?.caseId?.name}
											readOnly
										/>
									</div>
								</div>
								<div className="col-6">
									<div className="form-group">
										<label>Consultant Doctor</label>
										<input
											className="form-control"
											placeholder="Consultant Doctor"
											value={patientDetails?.consultantDoctorId?.name}
											readOnly
										/>
									</div>
								</div>
								<div className="col-6">
									<div className="form-group">
										<label>ReferenceDoctor</label>
										<input
											className="form-control"
											placeholder="ReferenceDoctor"
											value={patientDetails?.referenceDoctorId?.name}
											readOnly
										/>
									</div>
								</div>
								<div className="col-6">
									<div className="form-group">
										<label>Address</label>
										<input
											className="form-control"
											placeholder="Address"
											value={patientDetails?.patientId?.address}
											readOnly
										/>
									</div>
								</div>
							</div>
							<div className="row">

								{/* Case */}
								<div className="col-md-4">
									<div className="form-group">

										<label>Case</label>
										<select className="form-control" {...register("caseId")}>
											<option value="">Select Case</option>
											{cases.map(c => (
												<option key={c._id} value={c._id}>
													{c.name}
												</option>
											))}
										</select>
									</div>
								</div>

								<DynamicFieldArray
									label="Complaints"
									name="complaints"
									control={control}
									register={register}
								/>

								<div className="col-12 mt-3">
									<div className="form-group">

										<label>History Of Present Illness</label>
										<textarea className="form-control" {...register("historyOfPresentIllness")} />
									</div>
								</div>

								<DynamicFieldArray
									label="Past History"
									name="pastHistory"
									control={control}
									register={register}
								/>

								<div className="col-12 mt-3">
									<div className="form-group">

										<label>Comorbid Conditions</label>
										<textarea className="form-control" {...register("comorbidConditions")} />
									</div>
								</div>

								<div className="col-12 mt-3">
									<div className="form-group">

										<label>Habits / Advice</label>
										<textarea className="form-control" {...register("habitsAdvice")} />
									</div>
								</div>

								<DynamicFieldArray
									label="Family History"
									name="familyHistory"
									control={control}
									register={register}
								/>

								<div className="col-12 mt-3">
									<div className="form-group">

										<label>Medication Being Taken</label>
										<textarea className="form-control" {...register("medicationBeingTaken")} />
									</div>
								</div>

								<DynamicFieldArray
									label="Examinations"
									name="examinations"
									control={control}
									register={register}
								/>

								<div className="col-12 mt-3">
									<div className="form-group">

										<label>Known Allergies</label>
										<textarea className="form-control" {...register("anyKnownAllergies")} />
									</div>
								</div>

								<div className="col-12 mt-3">
									<div className="form-group">

										<label>Diagnosis *</label>
										<textarea className="form-control" {...register("diagnosis")} />
										<p className="text-danger">{errors.diagnosis?.message}</p>
									</div>
								</div>

								<div className="col-12 mt-3">
									<div className="form-group">

										<label>Special Comments / Next Follow Up</label>
										<textarea
											className="form-control"
											{...register("specialCommentsNextFollowUp")}
										/>
									</div>
								</div>

							</div>
						</div>

						<div className="card-footer text-end">
							<button type="submit" className="btn btn-primary">
								Submit
							</button>
						</div>
					</form>
        </div>
      </div>
    </Fragment>
  );
};

export default EditPrescriptionEntry;
